import React, { ChangeEvent } from "react";
import { TextField } from "@mui/material";
import { useTranslation } from "react-i18next";
import { UrAgent } from "../../../types/agentTypes";
import Provider from "../../../enum/Provider.enum";
import {
  getNormalizedOgrnip,
  isOgrnipTooLong,
  OGRNIP_MAX_LENGTH,
} from "../helpers";
import { FieldColumn, FieldRow, Section, SectionTitle } from "../styles";

type CompanySectionProps = {
  formData: UrAgent;
  provider: Provider;
  isOgrnipOverflow: boolean;
  setIsOgrnipOverflow: React.Dispatch<React.SetStateAction<boolean>>;
  setFormData: React.Dispatch<React.SetStateAction<any>>;
  onChange: (
    e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>,
  ) => void;
};

const CompanySection = ({
  formData,
  provider,
  isOgrnipOverflow,
  setIsOgrnipOverflow,
  setFormData,
  onChange,
}: CompanySectionProps) => {
  const { t } = useTranslation();
  const isOgrnipInvalid = isOgrnipOverflow || isOgrnipTooLong(formData.ogrnip);

  return (
    <Section>
      <SectionTitle>{t("createAgent.company.title")}</SectionTitle>

      <FieldRow>
        <FieldColumn>
          <TextField
            size="small"
            label={t("createAgent.company.fullName")}
            name="full_name"
            value={formData.full_name ?? ""}
            fullWidth
            required
            onChange={onChange}
          />
        </FieldColumn>

        {provider === Provider.ARMENIA && (
          <FieldColumn>
            <TextField
              size="small"
              label={t("createAgent.company.fullNameAm")}
              name="full_name_am"
              value={formData.full_name_am ?? ""}
              fullWidth
              required
              onChange={onChange}
            />
          </FieldColumn>
        )}

        <FieldColumn>
          <TextField
            size="small"
            label={t("createAgent.company.shortName")}
            name="lite_name"
            value={formData.lite_name ?? ""}
            fullWidth
            required
            onChange={onChange}
          />
        </FieldColumn>
      </FieldRow>

      <FieldRow>
        <FieldColumn>
          <TextField
            size="small"
            label={t("createAgent.company.legalAddress")}
            name="urid_adress"
            value={formData.urid_adress ?? ""}
            fullWidth
            required
            onChange={onChange}
          />
        </FieldColumn>

        {provider === Provider.ARMENIA && (
          <FieldColumn>
            <TextField
              size="small"
              label={t("createAgent.company.legalAddressAm")}
              name="urid_adress_am"
              value={formData.urid_adress_am ?? ""}
              fullWidth
              required
              onChange={onChange}
            />
          </FieldColumn>
        )}
      </FieldRow>

      <FieldRow>
        <FieldColumn>
          <TextField
            size="small"
            label={t("createAgent.company.ogrnip")}
            name="ogrnip"
            value={formData.ogrnip ?? ""}
            fullWidth
            required
            error={isOgrnipInvalid}
            helperText={
              isOgrnipInvalid
                ? t("createAgent.validation.ogrnipMaxLength")
                : " "
            }
            onChange={(event: ChangeEvent<HTMLInputElement>) => {
              const normalizedValue = getNormalizedOgrnip(event.target.value);
              setIsOgrnipOverflow(normalizedValue.length > OGRNIP_MAX_LENGTH);

              setFormData((prev: any) => ({
                ...prev,
                ogrnip: normalizedValue.slice(0, OGRNIP_MAX_LENGTH),
              }));
            }}
            inputProps={{ maxLength: OGRNIP_MAX_LENGTH, inputMode: "numeric" }}
          />
        </FieldColumn>
      </FieldRow>
    </Section>
  );
};

export default CompanySection;
